import React, { useLayoutEffect } from 'react';
import './SettingsPage.css';

import { useAuth } from '../../hooks/useAuth';

import FiltersIcon from '../../assets/Icons/FiltersIcon.svg?react';

import Text from '../../components/Text/Text';
import Flex from '../../components/Flex/Flex';

import Button from '../../components/Button/Button';
import TableSizeFilter from '../../components/TableSizeFilter/TableSizeFilter';
import LimitFilter from '../../components/LimitFilter/LimitsFilter';
import DateFilter, { CustomDateFilterValue } from '../../components/DateFilter/DateFilter';
import {
  getPresetFiltersLocalStorage,
  setPresetFiltersLocalStorage,
} from '../../localStorage/presetFiltersLocalStorage';
import RandomizerForm from '../../components/RandomizerForm/RandomizerForm';
import { useTranslation } from 'react-i18next';
import Checkbox, { CheckboxChangeEvent } from 'rc-checkbox';
import { Form, Select, Switch } from 'antd';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined, SettingOutlined } from '@ant-design/icons';
import ProfileIcon from '../../assets/Icons/ProfileIcon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../App/selectors';
import { globalSlice } from '../App/reducer';
import {
  formatInactiveTabCloseOptionLabel,
  getInactiveTabCloseEnabledLocalStorage,
  getInactiveTabCloseLocalStorage,
  INACTIVE_TAB_CLOSE_CHANGED_EVENT,
  INACTIVE_TAB_CLOSE_OPTIONS,
  InactiveTabCloseTimeoutSec,
  setInactiveTabCloseEnabledLocalStorage,
  setInactiveTabCloseLocalStorage,
} from '../../localStorage/inactiveTabCloseLocalStorage';

import type { PokerSiteId as PokersiteId } from '@poker-stats/contracts';

const pokersiteOptions: Array<{ label: string; value: PokersiteId }> = [
  { label: 'PS', value: 1 },
  { label: 'CHI', value: 2 },
  { label: 'WPN', value: 3 },
  { label: '888', value: 4 },
  { label: 'CPR', value: 5 },
  { label: 'IP', value: 6 },
];

const defaultAutopasteRoomPriority: Array<PokersiteId> = [1, 2, 3, 4, 5, 6];

type AutopasteRoomPriorityChipProps = {
  index: number;
  label: string;
  roomId: PokersiteId;
};

const AutopasteRoomPriorityChip = React.memo(
  ({ index, label, roomId }: AutopasteRoomPriorityChipProps) => {
    const {
      attributes,
      listeners,
      setActivatorNodeRef,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: roomId });

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? 'none' : transition,
    };

    return (
      <div
        ref={setNodeRef}
        className={`SettingsPage__AutopasteRoomChip${
          isDragging ? ' SettingsPage__AutopasteRoomChip--dragging' : ''
        }`}
        style={style}
      >
        <span className="SettingsPage__AutopasteRoomChipPriority">{index + 1}</span>
        <span className="SettingsPage__AutopasteRoomChipLabel">{label}</span>
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="SettingsPage__AutopasteRoomChipHandle"
          aria-label={`Move ${label}`}
          {...attributes}
          {...listeners}
        >
          <HolderOutlined />
        </button>
      </div>
    );
  },
);

type SettingsPageProps = {
  embedded?: boolean;
};

const SettingsPage = React.memo(({ embedded = false }: SettingsPageProps) => {
  const { t } = useTranslation('translation');
  useAuth();

  const user = useSelector(selectUser);

  const dispatch = useDispatch();

  const [tableSizeFilterValue, setTableSizeFilterValue] = React.useState<number | undefined>();

  const [limitFilterValue, setLimitFilterValue] = React.useState<number>(0);

  const [dateFilterValue, setDateFilterValue] = React.useState<number | undefined>();

  const [customDateFilterValue, setCustomDateFilterValue] = React.useState<
    CustomDateFilterValue | undefined
  >();

  const [smartSearch, setSmartSearch] = React.useState<boolean>(
    localStorage.getItem('smart-search') === 'true',
  );

  const [inactiveTabCloseTimeoutSec, setInactiveTabCloseTimeoutSec] =
    React.useState<InactiveTabCloseTimeoutSec>(getInactiveTabCloseLocalStorage());

  const [inactiveTabCloseEnabled, setInactiveTabCloseEnabled] = React.useState<boolean>(
    getInactiveTabCloseEnabledLocalStorage(),
  );

  const handleChangeSmartSearch = React.useCallback((e: CheckboxChangeEvent) => {
    setSmartSearch(e.target.checked);
    localStorage.setItem('smart-search', String(e.target.checked));
  }, []);

  const handleChangeInactiveTabClose = React.useCallback((value: InactiveTabCloseTimeoutSec) => {
    setInactiveTabCloseTimeoutSec(value);
    setInactiveTabCloseLocalStorage(value);
  }, []);

  const handleChangeInactiveTabCloseEnabled = React.useCallback((checked: boolean) => {
    setInactiveTabCloseEnabled(checked);
    setInactiveTabCloseEnabledLocalStorage(checked);
  }, []);

  const inactiveTabCloseOptions = React.useMemo(
    () =>
      INACTIVE_TAB_CLOSE_OPTIONS.map((timeoutSec) => ({
        value: timeoutSec,
        label: formatInactiveTabCloseOptionLabel(timeoutSec, t),
      })),
    [t],
  );

  useLayoutEffect(() => {
    const { tableSizeFilterValue, limitFilterValue, dateFilterValue, customDateFilterValue } =
      getPresetFiltersLocalStorage();

    setTableSizeFilterValue(tableSizeFilterValue);
    setLimitFilterValue(limitFilterValue);
    setDateFilterValue(dateFilterValue);
    setCustomDateFilterValue(customDateFilterValue);
  }, []);

  React.useEffect(() => {
    const syncInactiveTabClose = () => {
      setInactiveTabCloseEnabled(getInactiveTabCloseEnabledLocalStorage());
      setInactiveTabCloseTimeoutSec(getInactiveTabCloseLocalStorage());
    };

    window.addEventListener('storage', syncInactiveTabClose);
    window.addEventListener(INACTIVE_TAB_CLOSE_CHANGED_EVENT, syncInactiveTabClose);

    return () => {
      window.removeEventListener('storage', syncInactiveTabClose);
      window.removeEventListener(INACTIVE_TAB_CLOSE_CHANGED_EVENT, syncInactiveTabClose);
    };
  }, []);

  const handleChangeTableSizeFilter = React.useCallback((value?: number) => {
    setTableSizeFilterValue(value);
  }, []);

  const handleChangeLimitFilter = React.useCallback((value: number) => {
    setLimitFilterValue(value);
  }, []);

  const handleDateFilter = React.useCallback(
    (value?: number, customValue?: CustomDateFilterValue) => {
      setDateFilterValue(value);
      setCustomDateFilterValue(customValue);
    },
    [],
  );

  const handleApply = React.useCallback(() => {
    setPresetFiltersLocalStorage(
      tableSizeFilterValue,
      limitFilterValue,
      dateFilterValue,
      customDateFilterValue,
    );
  }, [tableSizeFilterValue, limitFilterValue, dateFilterValue, customDateFilterValue]);

  const handleReset = React.useCallback(() => {
    setTableSizeFilterValue(undefined);
    setLimitFilterValue(0);
    setDateFilterValue(undefined);
    setCustomDateFilterValue(undefined);
  }, []);

  const autopasteRoomPriority = user?.autopasteRoomPriority || defaultAutopasteRoomPriority;

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 1 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleAutopasteRoomDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = autopasteRoomPriority.indexOf(active.id as PokersiteId);
      const newIndex = autopasteRoomPriority.indexOf(over.id as PokersiteId);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return;
      }

      dispatch(
        globalSlice.actions.changeAutopasteRoomPriority(
          arrayMove(autopasteRoomPriority, oldIndex, newIndex),
        ),
      );
    },
    [autopasteRoomPriority, dispatch],
  );

  const panelClass = embedded
    ? 'settings-panel SettingsPage__Content'
    : 'SurfaceCard SettingsPage__Content';

  const renderPanelHead = (icon: React.ReactNode, title: string) =>
    embedded ? (
      <div className="settings-panel__head">
        <span className="settings-panel__icon">{icon}</span>
        <h2>{title}</h2>
      </div>
    ) : (
      <Flex align="center" gap={12}>
        <span className="SurfaceCard__icon SurfaceCard__icon--blue SettingsPage__FiltersIcon">
          {icon}
        </span>
        <Text size={4} bold>
          {title}
        </Text>
      </Flex>
    );

  const renderPanelActions = (onReset: () => void, onApply: () => void) =>
    embedded ? (
      <div className="settings-panel__footer">
        <button type="button" className="outline-button" onClick={onReset}>
          {t('reset')}
        </button>
        <button type="button" className="acid-button" onClick={onApply}>
          {t('apply')} <span aria-hidden="true">↗</span>
        </button>
      </div>
    ) : (
      <div className="FilterPanel__actions">
        <Button onClick={onReset}>{t('reset')}</Button>
        <Button type="primary" onClick={onApply}>
          {t('apply')}
        </Button>
      </div>
    );

  const content = (
    <>
      <section className={panelClass}>
        {renderPanelHead(<FiltersIcon />, t('preset_filters'))}
        {embedded ? (
          <div className="settings-filters-grid">
            <TableSizeFilter value={tableSizeFilterValue} onChange={handleChangeTableSizeFilter} />
            <LimitFilter value={limitFilterValue} onChange={handleChangeLimitFilter} />
            <div className="settings-filter-span">
              <DateFilter
                value={dateFilterValue}
                customValue={customDateFilterValue}
                onChange={handleDateFilter}
              />
            </div>
          </div>
        ) : (
          <Flex gap={32} wrap>
            <TableSizeFilter value={tableSizeFilterValue} onChange={handleChangeTableSizeFilter} />
            <LimitFilter value={limitFilterValue} onChange={handleChangeLimitFilter} />
            <DateFilter
              value={dateFilterValue}
              customValue={customDateFilterValue}
              onChange={handleDateFilter}
            />
          </Flex>
        )}
        {renderPanelActions(handleReset, handleApply)}
      </section>

      <RandomizerForm embedded={embedded} />

      <section className={panelClass}>
        {embedded ? renderPanelHead(<ProfileIcon />, 'Поиск и autopaste') : null}
        {embedded ? (
          <div className="settings-fields">
            <label className="settings-toggle-row CheckboxField">
              <Checkbox checked={smartSearch} onChange={handleChangeSmartSearch} />
              <span>{t('smart_search')}</span>
            </label>

            <div className="settings-subsection">
              <p className="settings-field-label">{t('rooms_for_search')}</p>
              <Select
                mode="multiple"
                options={pokersiteOptions}
                size="large"
                value={user?.roomsToSearch}
                onChange={(v) => dispatch(globalSlice.actions.changeRoomsToSearch(v))}
              />
            </div>

            <div className="settings-subsection">
              <p className="settings-field-label">{t('autopaste_room_priority')}</p>
              <p className="settings-field-hint">Перетащите румы, чтобы задать порядок автопаста</p>
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleAutopasteRoomDragEnd}
              >
                <SortableContext items={autopasteRoomPriority} strategy={rectSortingStrategy}>
                  <div className="SettingsPage__AutopasteRoomPriority">
                    {autopasteRoomPriority.map((roomId, index) => {
                      const room = pokersiteOptions.find((option) => option.value === roomId)!;

                      return (
                        <AutopasteRoomPriorityChip
                          key={roomId}
                          index={index}
                          label={room.label}
                          roomId={roomId}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div className="settings-subsection">
              <p className="settings-field-label">{t('inactive_tab_close')}</p>
              <p className="settings-field-hint">{t('inactive_tab_close_hint')}</p>
              <label className="settings-toggle-row CheckboxField">
                <Switch
                  checked={inactiveTabCloseEnabled}
                  onChange={handleChangeInactiveTabCloseEnabled}
                />
                <span>{t('inactive_tab_close_enabled')}</span>
              </label>
              <Select
                options={inactiveTabCloseOptions}
                size="large"
                value={inactiveTabCloseTimeoutSec}
                disabled={!inactiveTabCloseEnabled}
                onChange={handleChangeInactiveTabClose}
              />
            </div>
          </div>
        ) : (
          <Flex vertical gap={16}>
            <label className="CheckboxField">
              <Checkbox checked={smartSearch} onChange={handleChangeSmartSearch} />
              <Text size={3} bold>
                {t('smart_search')}
              </Text>
            </label>
            <Form.Item label={t('rooms_for_search')} layout="vertical">
              <Select
                mode="multiple"
                options={pokersiteOptions}
                size="large"
                value={user?.roomsToSearch}
                onChange={(v) => dispatch(globalSlice.actions.changeRoomsToSearch(v))}
              />
            </Form.Item>
            <Form.Item label={t('autopaste_room_priority')} layout="vertical">
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleAutopasteRoomDragEnd}
              >
                <SortableContext items={autopasteRoomPriority} strategy={rectSortingStrategy}>
                  <div className="SettingsPage__AutopasteRoomPriority">
                    {autopasteRoomPriority.map((roomId, index) => {
                      const room = pokersiteOptions.find((option) => option.value === roomId)!;

                      return (
                        <AutopasteRoomPriorityChip
                          key={roomId}
                          index={index}
                          label={room.label}
                          roomId={roomId}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </Form.Item>
            <Form.Item
              label={t('inactive_tab_close')}
              layout="vertical"
              extra={t('inactive_tab_close_hint')}
            >
              <Flex vertical gap={16}>
                <label className="CheckboxField">
                  <Switch
                    checked={inactiveTabCloseEnabled}
                    onChange={handleChangeInactiveTabCloseEnabled}
                  />
                  <Text size={3} bold>
                    {t('inactive_tab_close_enabled')}
                  </Text>
                </label>
                <Select
                  options={inactiveTabCloseOptions}
                  size="large"
                  value={inactiveTabCloseTimeoutSec}
                  disabled={!inactiveTabCloseEnabled}
                  onChange={handleChangeInactiveTabClose}
                />
              </Flex>
            </Form.Item>
          </Flex>
        )}
      </section>
    </>
  );

  if (embedded) {
    return <div className="settings-stack SettingsPage">{content}</div>;
  }

  return (
    <div className="PageShell SettingsPage">
      <header className="PageHero">
        <div className="PageHero__glow" aria-hidden />
        <div className="PageHero__content">
          <span className="PageHero__eyebrow">
            <SettingOutlined /> Fish Hunter
          </span>
          <Text size={6} bold>
            {t('settings')}
          </Text>
          <Text type="secondary" size={3}>
            Пресеты фильтров, поиск и приоритет румов для autopaste
          </Text>
        </div>
      </header>
      {content}
    </div>
  );
});

export default SettingsPage;
